// src/services/apiUtils.ts
//
// Canonical fetch wrapper for the Admin Panel — every feature
// `api/index.ts` routes through this. The companion axios layer at
// `./config.ts` is kept ONLY for the product/brand/category/upload
// flows that depend on its multipart + interceptor ergonomics; both
// layers share the same `tokens` key in localStorage and the same
// refresh endpoint so 401 handling is consistent across the app.

import { baseURL } from "./apiConfigMain";
import { ApiResponse, ErrorResponse } from "./types";

type Tokens = { accessToken: string | null; refreshToken: string | null };

const getTokens = (): Tokens => {
  try {
    const tokens = localStorage.getItem("tokens");
    if (tokens) {
      const parsed = JSON.parse(tokens);
      return {
        accessToken: parsed.accessToken || null,
        refreshToken: parsed.refreshToken || null,
      };
    }
  } catch (error) {
    console.warn('Error parsing tokens from localStorage:', error);
  }
  return { accessToken: null, refreshToken: null };
};

// Per-request UUID for request-id propagation (audit fix #44). The
// backend echoes X-Request-ID back; logs from both sides can be
// joined on the same id during incident triage. Without this, the
// only correlation hook was timestamp+endpoint which broke down the
// moment two admins hit the same route in the same second.
const generateRequestId = (): string => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return (crypto as Crypto).randomUUID();
  }
  return `req_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
};

const getHeaders = (customHeaders: HeadersInit = {}): HeadersInit => {
  const { accessToken } = getTokens();
  return {
    "Content-Type": "application/json",
    "X-Request-ID": generateRequestId(),
    ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
    ...customHeaders,
  };
};

// Concurrent 401s should not each trigger a separate refresh — one
// in-flight refresh promise is shared by all callers, then resolved
// in-order so they retry against the new access token together.
let refreshPromise: Promise<Tokens | null> | null = null;

const refreshAccessToken = async (): Promise<Tokens | null> => {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const { refreshToken } = getTokens();
    if (!refreshToken) return null;

    try {
      const resp = await fetch(`${baseURL}/auth/refresh-token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken, userType: "admin" }),
      });
      if (!resp.ok) return null;
      const json = await resp.json();
      const newTokens: Tokens = json?.data?.tokens || json?.data || json;
      if (!newTokens?.accessToken) return null;
      // Preserve the existing user blob; only swap tokens.
      localStorage.setItem("tokens", JSON.stringify(newTokens));
      return newTokens;
    } catch (err) {
      console.warn("Token refresh failed:", err);
      return null;
    } finally {
      // Reset so a subsequent expiry can try again later.
      setTimeout(() => { refreshPromise = null; }, 0);
    }
  })();

  return refreshPromise;
};

const forceLogout = (): void => {
  localStorage.removeItem("tokens");
  localStorage.removeItem("user");
  // Reload to /login — admin SPA's ProtectedRoute will block the
  // route if it tries to navigate without auth.
  if (typeof window !== "undefined" && window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
};

const handleResponse = async <T>(response: Response): Promise<ApiResponse<T>> => {
  const json = await response.json();

  if (!response.ok) {
    const error: ErrorResponse = {
      success: false,
      message: json.message || "An error occurred",
      error: json.error,
    };
    throw new Error(error.message);
  }

  return json;
};

// Issue a request and, if it 401s, attempt one refresh-and-retry
// pass. The retry sends a fresh Authorization header on top of
// whatever customHeaders were originally passed.
const fetchWithAuthRetry = async (
  input: string,
  init: RequestInit,
  alreadyRetried = false,
): Promise<Response> => {
  const response = await fetch(input, init);
  if (response.status !== 401 || alreadyRetried) return response;

  const refreshed = await refreshAccessToken();
  if (!refreshed?.accessToken) {
    forceLogout();
    return response;
  }

  const retryHeaders = {
    ...(init.headers as Record<string, string> | undefined),
    Authorization: `Bearer ${refreshed.accessToken}`,
  };
  return fetchWithAuthRetry(input, { ...init, headers: retryHeaders }, true);
};

const createQueryString = (params: Record<string, any>): string => {
  return Object.keys(params)
    .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
    .join("&");
};

const apiUtils = {
  get: async <T>(endpoint: string, params: Record<string, any> = {}, customHeaders?: HeadersInit): Promise<ApiResponse<T>> => {
    let url = `${baseURL}${endpoint}`;
    if (Object.keys(params).length > 0) {
      url += `?${createQueryString(params)}`;
    }

    const response = await fetchWithAuthRetry(url, {
      method: "GET",
      headers: getHeaders(customHeaders),
    });

    return handleResponse<T>(response);
  },

  post: async <T>(endpoint: string, data: any, customHeaders?: HeadersInit): Promise<ApiResponse<T>> => {
    const isFormData = data instanceof FormData;

    const response = await fetchWithAuthRetry(`${baseURL}${endpoint}`, {
      method: "POST",
      headers: getHeaders(
        isFormData
          ? {} // Browser will auto-set content-type for FormData
          : { "Content-Type": "application/json", ...customHeaders }
      ),
      body: isFormData ? data : JSON.stringify(data),
    });

    return handleResponse<T>(response);
  },

  postFormData: async <T>(endpoint: string, formData: FormData): Promise<ApiResponse<T>> => {
    const { accessToken } = getTokens();

    const response = await fetchWithAuthRetry(`${baseURL}${endpoint}`, {
      method: "POST",
      headers: {
        ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
        // DO NOT manually set Content-Type for FormData
      },
      body: formData,
    });

    return handleResponse<T>(response);
  },

  putFormData: async <T>(endpoint: string, formData: FormData): Promise<ApiResponse<T>> => {
    const { accessToken } = getTokens();

    const response = await fetchWithAuthRetry(`${baseURL}${endpoint}`, {
      method: "PUT",
      headers: {
        ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
        // DO NOT manually set Content-Type for FormData
      },
      body: formData,
    });

    return handleResponse<T>(response);
  },


  put: async <T>(endpoint: string, data: any, customHeaders?: HeadersInit): Promise<ApiResponse<T>> => {
    const isFormData = data instanceof FormData;

    const response = await fetchWithAuthRetry(`${baseURL}${endpoint}`, {
      method: "PUT",
      headers: getHeaders(
        isFormData
          ? {}
          : { "Content-Type": "application/json", ...customHeaders }
      ),
      body: isFormData ? data : JSON.stringify(data),
    });

    return handleResponse<T>(response);
  },

  patch: async <T>(endpoint: string, data: any, customHeaders?: HeadersInit): Promise<ApiResponse<T>> => {
    const isFormData = data instanceof FormData;

    const response = await fetchWithAuthRetry(`${baseURL}${endpoint}`, {
      method: "PATCH",
      headers: getHeaders(
        isFormData
          ? {}
          : { "Content-Type": "application/json", ...customHeaders }
      ),
      body: isFormData ? data : JSON.stringify(data),
    });

    return handleResponse<T>(response);
  },

  delete: async <T>(endpoint: string, customHeaders?: HeadersInit): Promise<ApiResponse<T>> => {
    const response = await fetchWithAuthRetry(`${baseURL}${endpoint}`, {
      method: "DELETE",
      headers: getHeaders(customHeaders),
    });

    return handleResponse<T>(response);
  },
};

export default apiUtils;
